import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { RangeMode, RangeUnit, TimeRange } from '@/types'

export interface RangeState {
  mode: RangeMode
  unit: RangeUnit
  count: number
  from: string
  to: string
  tick: number
}

export const RANGE_BUCKETS = 200

export const RANGE_UNIT_MS: Record<RangeUnit, number> = {
  minute: 60_000,
  hour: 60 * 60_000,
  day: 24 * 60 * 60_000,
  week: 7 * 24 * 60 * 60_000
}

const RANGE_UNIT_OF: Record<string, RangeUnit> = { m: 'minute', h: 'hour', d: 'day', w: 'week' }

export const RANGE_UNIT_MAX: Record<RangeUnit, number> = {
  minute: 60,
  hour: 48,
  day: 90,
  week: 52
}

export const stepFor = (fromMs: number, toMs: number): string =>
  `${Math.max(1, Math.round((toMs - fromMs) / 1000 / RANGE_BUCKETS))}s`

const toRange = (from: number, to: number): TimeRange => ({
  from: new Date(from).toISOString(),
  to: new Date(to).toISOString(),
  step: stepFor(from, to)
})

export const resolveRange = (state: RangeState): TimeRange | null => {
  void state.tick
  if (state.mode === 'live') return null
  if (state.mode === 'absolute') {
    const from = new Date(state.from).getTime()
    const to = new Date(state.to).getTime()
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null
    return toRange(from, to)
  }
  const now = Date.now()
  return toRange(now - state.count * RANGE_UNIT_MS[state.unit], now)
}

export const useRangeStore = defineStore('range', () => {
  const state = ref<RangeState>({ mode: 'live', unit: 'minute', count: 15, from: '', to: '', tick: 0 })

  const range = computed(() => resolveRange(state.value))

  const setRangeLive = () => {
    state.value = { ...state.value, mode: 'live', tick: state.value.tick + 1 }
  }

  const setRangeRelative = (unit: RangeUnit, count: number) => {
    const bounded = Math.max(1, Math.min(RANGE_UNIT_MAX[unit], Math.round(count)))
    state.value = { ...state.value, mode: 'relative', unit, count: bounded, tick: state.value.tick + 1 }
  }

  const setRangeAbsolute = (from: string, to: string) => {
    state.value = { ...state.value, mode: 'absolute', from, to, tick: state.value.tick + 1 }
  }

  const setRangeQuery = (from: unknown, to: unknown) => {
    const relative = typeof from === 'string' && to == null ? /^(\d+)([mhdw])$/.exec(from) : null
    if (relative) setRangeRelative(RANGE_UNIT_OF[relative[2]], Number(relative[1]))
    else if (typeof from === 'string' && typeof to === 'string') setRangeAbsolute(from, to)
  }

  const stepRange = (direction: -1 | 1) => {
    const current = range.value ?? resolveRange({ ...state.value, mode: 'relative' })
    if (!current) return
    const from = new Date(current.from).getTime()
    const to = new Date(current.to).getTime()
    const width = direction * (to - from)
    setRangeAbsolute(new Date(from + width).toISOString(), new Date(to + width).toISOString())
  }

  const refreshRange = () => {
    state.value = { ...state.value, tick: state.value.tick + 1 }
  }

  return { state, range, setRangeLive, setRangeRelative, setRangeAbsolute, setRangeQuery, stepRange, refreshRange }
})
